import { isProductionHistoryWindow } from "@/features/devices/types";
import { parseAddress, parseTag } from "@/features/machines/addressing";
import { isGhmSignalKind } from "@ghm/mqtt-contract";
import { cellsFromLegacySize } from "./cellGrid";
import type { DashboardWidget, WidgetLayout, WidgetSettings, WidgetType } from "./types";
import { normalizeStateMappings } from "./stateMappings";
import { WIDGET_REGISTRY } from "./registry";

const WIDGET_TYPES = new Set<WidgetType>(WIDGET_REGISTRY.map((definition) => definition.type));
const WIDGET_SIZES = new Set<WidgetLayout["size"]>(["sm", "md", "lg"]);

export function getDashboardWidgetStorageKey(
  workspaceId: string,
  machineId: string,
  configVersion?: string | null,
  userId?: string | null,
): string {
  const userScope = userId?.trim();
  const base = userScope
    ? `ghm:dashboard-widgets:${userScope}:${workspaceId}:${machineId}`
    : getLegacyDashboardWidgetStorageKey(workspaceId, machineId);
  const version = configVersion?.trim();
  return version ? `${base}:${version}` : base;
}

export function getLegacyDashboardWidgetStorageKey(
  workspaceId: string,
  machineId: string,
  configVersion?: string | null,
): string {
  const base = `ghm:dashboard-widgets:${workspaceId}:${machineId}`;
  const version = configVersion?.trim();
  return version ? `${base}:${version}` : base;
}

export function normalizeDashboardWidgets(value: unknown, machineId: string): DashboardWidget[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((candidate): DashboardWidget[] => {
    if (!candidate || typeof candidate !== "object") return [];
    const item = candidate as Record<string, unknown>;
    const widgetType = item.widgetType;
    const source = item.source;
    const title = item.title;

    if (typeof widgetType !== "string" || !WIDGET_TYPES.has(widgetType as WidgetType)) return [];
    if (typeof source !== "string" || (!parseTag(source) && !parseAddress(source))) return [];

    const extraSources = normalizeExtraSources(item.extraSources, source);

    return [
      {
        id: typeof item.id === "string" && item.id ? item.id : `widget-${source}`,
        machineId,
        title: typeof title === "string" && title.trim() ? title.trim() : source,
        source,
        ...(extraSources.length > 0 ? { extraSources } : null),
        ...(typeof item.section === "string" && item.section.trim()
          ? { section: item.section.trim() }
          : null),
        widgetType: widgetType as WidgetType,
        settings: normalizeSettings(item.settings, source, widgetType as WidgetType),
        layout: normalizeLayout(item.layout, widgetType as WidgetType),
      },
    ];
  });
}

export function loadLocalDashboardWidgets(
  storage: Pick<Storage, "getItem">,
  key: string,
  machineId: string,
): DashboardWidget[] {
  const raw = storage.getItem(key);
  if (!raw) return [];

  try {
    return normalizeDashboardWidgets(JSON.parse(raw), machineId);
  } catch {
    return [];
  }
}

export function saveLocalDashboardWidgets(
  storage: Pick<Storage, "setItem">,
  key: string,
  widgets: DashboardWidget[],
) {
  storage.setItem(key, JSON.stringify(widgets));
}

/**
 * Fontes extras de um widget multi-grandeza. Aplica à lista o MESMO rigor da
 * âncora — uma tag que não parseia é descartada individualmente, sem levar o
 * widget junto —, remove duplicatas e a própria âncora.
 *
 * Descarta a fonte ruim em vez do card inteiro pelo mesmo motivo do parser de
 * envelope MQTT: um painel trifásico que perde uma fase por tag inválida
 * ainda é útil com duas; sumir com o card não é.
 *
 * O que NÃO é checado aqui: se as fontes combinam entre si (mesma unidade e
 * `kind`). Isso depende do `/meta` atual, que a normalização não enxerga — é
 * responsabilidade do editor na hora de compor, e do card na hora de exibir.
 */
function normalizeExtraSources(value: unknown, anchor: string): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>([anchor]);
  const out: string[] = [];
  for (const candidate of value) {
    if (typeof candidate !== "string") continue;
    const source = candidate.trim();
    if (!source || seen.has(source)) continue;
    if (!parseTag(source) && !parseAddress(source)) continue;
    seen.add(source);
    out.push(source);
  }
  return out;
}

/**
 * A origem de um grafico gerado, para o "Ocultar grafico" ter para onde voltar.
 *
 * `normalizeSettings` e uma allowlist: campo que nao passa por aqui e apagado
 * na proxima gravacao. Sem esta funcao o botao de desfazer sumiria sozinho na
 * primeira vez que o card fosse moldado ou movido.
 */
function normalizeChartOrigin(value: unknown): WidgetSettings["chartOrigin"] {
  if (!value || typeof value !== "object") return undefined;
  const origem = value as Record<string, unknown>;
  const widgetType = origem.widgetType;
  const title = origem.title;
  if (typeof widgetType !== "string" || !WIDGET_TYPES.has(widgetType as WidgetType)) {
    return undefined;
  }
  if (typeof title !== "string" || !title.trim()) return undefined;
  // A janela da origem passa pela MESMA allowlist do resto: o grafico
  // sobrescreve `history`, entao a janela do card so sobrevive ate o desfazer
  // se atravessar daqui. Sem esta linha ela morreria na primeira vez que o card
  // fosse movido — que e exatamente o que o comentario acima descreve.
  const history = normalizeHistory(origem.history);
  const snapshot = normalizeChartOriginSnapshot(origem.snapshot, widgetType as WidgetType);
  return {
    widgetType: widgetType as WidgetType,
    title,
    ...(history ? { history } : null),
    ...(snapshot ? { snapshot } : null),
  };
}

function normalizeChartOriginSnapshot(
  value: unknown,
  widgetType: WidgetType,
): NonNullable<WidgetSettings["chartOrigin"]>["snapshot"] {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const snapshot = value as Record<string, unknown>;
  // Sem esses campos nao ha snapshot completo. Nao fabricar um layout padrao
  // ou settings vazias para uma origem legada/incompleta.
  if (snapshot.version !== 1) return undefined;
  if (typeof snapshot.source !== "string" || (!parseTag(snapshot.source) && !parseAddress(snapshot.source))) {
    return undefined;
  }
  if (!snapshot.settings || typeof snapshot.settings !== "object" || Array.isArray(snapshot.settings)) {
    return undefined;
  }
  if (!snapshot.layout || typeof snapshot.layout !== "object" || Array.isArray(snapshot.layout)) {
    return undefined;
  }
  const layout = snapshot.layout as Record<string, unknown>;
  if (layout.size !== "wide" && !WIDGET_SIZES.has(layout.size as WidgetLayout["size"])) {
    return undefined;
  }
  if (integerOrUndefined(layout.order) === undefined) return undefined;
  const extraSources = normalizeExtraSources(snapshot.extraSources, snapshot.source);
  return {
    version: 1,
    source: snapshot.source,
    ...(extraSources.length > 0 ? { extraSources } : null),
    ...(typeof snapshot.section === "string" && snapshot.section.trim()
      ? { section: snapshot.section.trim() }
      : null),
    layout: normalizeLayout(snapshot.layout, widgetType),
    // Mesma allowlist, com origem DESLIGADA: dado salvo nao pode introduzir
    // snapshots recursivos nem transportar campos desconhecidos.
    settings: normalizeSettings(snapshot.settings, snapshot.source, widgetType, false),
  };
}

function normalizeSettings(
  value: unknown,
  source: string,
  widgetType: WidgetType,
  includeChartOrigin = true,
): WidgetSettings {
  if (!value || typeof value !== "object") return {};
  const settings = value as Record<string, unknown>;
  const unit = normalizeWidgetUnit(settings.unit, source, widgetType);
  return {
    signalKind: isGhmSignalKind(settings.signalKind) ? settings.signalKind : undefined,
    // Ausente = titulo automatico. Widget salvo antes do campo nunca prova
    // escolha, entao o nome publicado pelo dispositivo volta a vencer.
    renamedByOperator: settings.renamedByOperator === true ? true : undefined,
    unit,
    min: finiteNumberOrUndefined(settings.min),
    max: finiteNumberOrUndefined(settings.max),
    decimals: integerOrUndefined(settings.decimals),
    thresholds: Array.isArray(settings.thresholds)
      ? settings.thresholds.flatMap((threshold) => {
          if (!threshold || typeof threshold !== "object") return [];
          const candidate = threshold as Record<string, unknown>;
          const at = finiteNumberOrUndefined(candidate.at);
          const tone = candidate.tone;
          if (at === undefined) return [];
          if (tone !== "ok" && tone !== "warn" && tone !== "danger" && tone !== "info") return [];
          const direction = candidate.direction === "below" ? "below" : undefined;
          return [
            {
              at,
              tone,
              ...(direction ? { direction } : null),
              label: typeof candidate.label === "string" ? candidate.label : undefined,
            },
          ];
        })
      : undefined,
    activeWhen: settings.activeWhen === 0 || settings.activeWhen === 1 ? settings.activeWhen : undefined,
    stateLabels: normalizeStateLabels(settings.stateLabels),
    stateMappings: normalizeStateMappings(settings.stateMappings),
    alarmEnabled: settings.alarmEnabled === true ? true : undefined,
    availability: normalizeAvailability(settings.availability),
    production: normalizeProduction(settings.production),
    history: normalizeHistory(settings.history),
    chart: normalizeChart(settings.chart),
    ...(includeChartOrigin ? { chartOrigin: normalizeChartOrigin(settings.chartOrigin) } : null),
    utilization: normalizeUtilization(settings.utilization),
    stopAlarm: normalizeStopAlarm(settings.stopAlarm),
  };
}

/**
 * Faixas de utilizacao. `warn` PRECISA ser <= `good` (verde acima de `good`,
 * amarelo acima de `warn`, vermelho abaixo).
 *
 * Par invalido e RECUSADO, nao remendado. Antes o `warn` era espremido para o
 * valor de `good` a cada carga do localStorage e regravado no proximo save:
 * quem digitasse 60/80 ficava com 60/60, sem faixa amarela nenhuma e sem
 * nenhum aviso de que a configuracao tinha sido reescrita. Devolver `undefined`
 * cai no default do widget — visivelmente diferente do que foi pedido, que e o
 * que faz o operador perceber e corrigir. E o mesmo criterio de
 * `normalizeChart`/`normalizeAvailability`, que ja recusam registros
 * incoerentes em vez de completa-los por conta propria.
 */
function normalizeUtilization(value: unknown): WidgetSettings["utilization"] {
  if (!value || typeof value !== "object") return undefined;
  const item = value as Record<string, unknown>;
  const good = finiteNumberOrUndefined(item.goodAtPercent);
  const warn = finiteNumberOrUndefined(item.warnAtPercent);
  if (good === undefined || warn === undefined) return undefined;
  const clamp = (candidate: number) => Math.max(0, Math.min(100, Math.round(candidate)));
  const safeGood = clamp(good);
  const safeWarn = clamp(warn);
  if (safeWarn > safeGood) return undefined;
  return { goodAtPercent: safeGood, warnAtPercent: safeWarn };
}

function normalizeStopAlarm(value: unknown): WidgetSettings["stopAlarm"] {
  if (!value || typeof value !== "object") return undefined;
  const item = value as Record<string, unknown>;
  if (item.enabled !== true) return undefined;
  const afterMinutes = finiteNumberOrUndefined(item.afterMinutes);
  if (afterMinutes === undefined) return undefined;
  return { enabled: true, afterMinutes: Math.max(0, Math.round(afterMinutes)) };
}

function normalizeWidgetUnit(
  value: unknown,
  source: string,
  widgetType: WidgetType,
): string | undefined {
  const unit = typeof value === "string" && value.trim() ? value.trim() : undefined;
  if (unit !== "%") return unit;

  // "%" pertence a visoes derivadas (availability/utilization), nao ao sinal
  // fisico acumulado. Um layout antigo conseguiu salvar um bar-chart de
  // G*_acc com unit "%", fazendo "Distancia acumulada" aparecer como
  // porcentagem. Ao carregar/persistir widgets, removemos esse vazamento.
  const tag = parseTag(source);
  const isPhysicalAccumulator = tag?.signal === "acc";
  if (isPhysicalAccumulator && widgetType !== "availability" && widgetType !== "utilization") {
    return undefined;
  }
  return unit;
}

function normalizeChart(value: unknown): WidgetSettings["chart"] {
  if (!value || typeof value !== "object") return undefined;
  const item = value as Record<string, unknown>;
  const kind = item.kind === "line" || item.kind === "bar" ? item.kind : undefined;
  const series =
    item.series === "rate" || item.series === "throughput" || item.series === "cumulative"
      ? item.series
      : undefined;
  const window = isProductionHistoryWindow(item.window) ? item.window : undefined;
  const yScale = item.yScale === "fixed" ? "fixed" : "auto";
  if (!kind || !series || !window) return undefined;
  return { kind, series, window, yScale };
}

function normalizeAvailability(value: unknown): WidgetSettings["availability"] {
  if (!value || typeof value !== "object") return undefined;
  const item = value as Record<string, unknown>;
  const mode = item.mode === "activity" ? "activity" : item.mode === "state" ? "state" : undefined;
  const window =
    item.window === "current" || isProductionHistoryWindow(item.window) ? item.window : undefined;
  if (!mode || !window) return undefined;
  return {
    mode,
    window,
    activeWhen: item.activeWhen === 0 || item.activeWhen === 1 ? item.activeWhen : undefined,
    minActiveValue: finiteNumberOrUndefined(item.minActiveValue),
  };
}

function normalizeProduction(value: unknown): WidgetSettings["production"] {
  if (!value || typeof value !== "object") return undefined;
  const item = value as Record<string, unknown>;
  const mode =
    item.mode === "rate" || item.mode === "hourly" || item.mode === "total"
      ? item.mode
      : undefined;
  if (!mode) return undefined;
  const window =
    item.window === "current" || isProductionHistoryWindow(item.window) ? item.window : undefined;
  const totalMode =
    item.totalMode === "physical" ||
    item.totalMode === "baseline" ||
    item.totalMode === "lot"
      ? item.totalMode
      : undefined;
  return {
    mode,
    multiplier: finiteNumberOrUndefined(item.multiplier),
    target: finiteNumberOrUndefined(item.target),
    window,
    totalMode,
  };
}

function normalizeHistory(value: unknown): WidgetSettings["history"] {
  if (!value || typeof value !== "object") return undefined;
  const item = value as Record<string, unknown>;
  const window = isProductionHistoryWindow(item.window) ? item.window : undefined;
  return window ? { window } : undefined;
}

function normalizeStateLabels(value: unknown): WidgetSettings["stateLabels"] {
  if (!value || typeof value !== "object") return undefined;
  const item = value as Record<string, unknown>;
  if (typeof item.on !== "string" || typeof item.off !== "string") return undefined;
  return {
    on: item.on.trim() || "Ligado",
    off: item.off.trim() || "Desligado",
    unavailable:
      typeof item.unavailable === "string" && item.unavailable.trim()
        ? item.unavailable.trim()
        : "Sem sinal",
  };
}

/** Contagem de celulas valida: inteiro >= 1. Qualquer outra coisa e ausencia. */
function cellCount(value: unknown): number | undefined {
  const n = integerOrUndefined(value);
  return n !== undefined && n >= 1 ? n : undefined;
}

/** Indice de celula valido: inteiro >= 0. Base zero, ao contrario da contagem. */
function cellIndex(value: unknown): number | undefined {
  const n = integerOrUndefined(value);
  return n !== undefined && n >= 0 ? n : undefined;
}

/**
 * MIGRACAO DE UMA VIA do enum de tamanho antigo.
 *
 * `size` (`sm`/`md`/`lg`) era o tamanho antes da malha de celulas. Ele nao
 * some do arquivo salvo — layouts antigos existem — mas para de ser CONSULTADO:
 * na primeira leitura vira `w`/`h`, e dali em diante o tamanho tem uma fonte
 * so.
 *
 * Isso encerra um defeito de dado: o editor derivava `size` do TIPO quando o
 * layout nao tinha `sizeExplicit`, e regravava. Abrir um card e salvar sem
 * mudar nada trocava `md` por `sm`. Nao mudava nada na tela — `widgetCells` ja
 * ignorava o enum sem `sizeExplicit` — mas gravava no storage uma escolha que
 * ninguem fez.
 */
function normalizeLayout(value: unknown, widgetType: WidgetType): WidgetLayout {
  if (!value || typeof value !== "object") {
    return { size: "md", order: 0 };
  }
  const layout = value as Record<string, unknown>;
  // Migração: o enum antigo aceitava `wide`. Converte para `lg` (que agora
  // é o tamanho que ocupa 2 colunas + valor grande).
  const rawSize = layout.size;
  const size =
    rawSize === "wide"
      ? "lg"
      : typeof rawSize === "string" && WIDGET_SIZES.has(rawSize as WidgetLayout["size"])
        ? (rawSize as WidgetLayout["size"])
        : "md";
  // A conversao acontece AQUI, e uma vez: um layout que escolheu tamanho no
  // enum antigo e ainda nao tem celulas ganha `w`/`h` equivalentes. Depois
  // disso `sizeExplicit` nao e mais gravado, porque nada mais o consulta.
  const w = cellCount(layout.w);
  const h = cellCount(layout.h);
  const migrado =
    layout.sizeExplicit === true && w === undefined && h === undefined
      ? cellsFromLegacySize(size, widgetType)
      : undefined;

  return {
    size,
    order: integerOrUndefined(layout.order) ?? 0,
    // Tamanho em celulas: a unica fonte de verdade depois da migracao.
    ...(w !== undefined ? { w } : migrado ? { w: migrado.w } : null),
    ...(h !== undefined ? { h } : migrado ? { h: migrado.h } : null),
    // Posicao: base zero, entao 0 e valido — `cellCount` exige >= 1 e nao
    // serve aqui.
    ...(cellIndex(layout.x) !== undefined ? { x: cellIndex(layout.x) } : null),
    ...(cellIndex(layout.y) !== undefined ? { y: cellIndex(layout.y) } : null),
  };
}

function finiteNumberOrUndefined(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function integerOrUndefined(value: unknown): number | undefined {
  return typeof value === "number" && Number.isInteger(value) ? value : undefined;
}
